/**
 * File Type Validation Utility
 * Implements whitelist-based file type validation for security
 */

// 許可されたファイルタイプの定義（ホワイトリスト）
export const ALLOWED_FILE_TYPES = {
  // ドキュメント
  'application/pdf': ['.pdf'],
  'application/msword': ['.doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
  'application/vnd.ms-excel': ['.xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'application/vnd.ms-powerpoint': ['.ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],

  // テキスト
  'text/plain': ['.txt'],
  'text/csv': ['.csv'],
  'text/markdown': ['.md'],

  // 画像
  'image/jpeg': ['.jpg', '.jpeg'],
  'image/png': ['.png'],
  'image/gif': ['.gif'],
  'image/webp': ['.webp'],

  // 圧縮（自己責任）
  'application/zip': ['.zip'],
  'application/x-zip-compressed': ['.zip'],
} as const

// 危険なファイルタイプ（ブラックリスト）
export const DANGEROUS_EXTENSIONS = [
  // 実行ファイル
  '.exe', '.com', '.bat', '.cmd', '.msi', '.app', '.dmg', '.sh', '.bash', '.run',

  // スクリプト
  '.js', '.vbs', '.vbe', '.ps1', '.ps2', '.psm1', '.jar', '.py', '.rb', '.php', '.asp', '.aspx', '.jsp',

  // ショートカット
  '.lnk', '.url', '.desktop',

  // その他危険
  '.scr', '.pif', '.dll', '.sys', '.drv', '.cpl', '.hta',

  // スクリプト可能
  '.html', '.htm', '.svg', '.xml', '.xsl', '.xslt',
] as const

/**
 * ファイル拡張子を取得（小文字）
 */
function getFileExtension(filename: string): string {
  const parts = filename.toLowerCase().split('.')
  return parts.length > 1 ? `.${parts[parts.length - 1]}` : ''
}

/**
 * ファイル名が危険な拡張子を持っているかチェック
 */
function hasDangerousExtension(filename: string): boolean {
  const ext = getFileExtension(filename)
  return DANGEROUS_EXTENSIONS.includes(ext as any)
}

/**
 * MIMEタイプが許可リストに含まれているかチェック
 */
function isAllowedMimeType(mimeType: string): boolean {
  // 空のMIMEタイプは拒否
  if (!mimeType || mimeType.trim() === '') {
    return false
  }

  // 許可リストに含まれているかチェック
  return Object.keys(ALLOWED_FILE_TYPES).includes(mimeType)
}

/**
 * ファイル拡張子とMIMEタイプが一致しているかチェック
 */
function isExtensionMatchingMimeType(filename: string, mimeType: string): boolean {
  const ext = getFileExtension(filename)
  const allowedExtensions = ALLOWED_FILE_TYPES[mimeType as keyof typeof ALLOWED_FILE_TYPES]

  if (!allowedExtensions) {
    return false
  }

  return (allowedExtensions as readonly string[]).includes(ext)
}

export interface ValidationResult {
  isValid: boolean
  error?: string
  errorCode?: 'DANGEROUS_EXTENSION' | 'INVALID_MIME_TYPE' | 'EXTENSION_MISMATCH' | 'EMPTY_FILE'
}

/**
 * ファイルをバリデーション（クライアント側）
 *
 * チェック項目:
 * 1. ファイルサイズが0でないか
 * 2. 危険な拡張子でないか（.exe, .bat, .js など）
 * 3. MIMEタイプが許可リストに含まれているか
 * 4. ファイル拡張子とMIMEタイプが一致しているか
 */
export function validateFile(file: File): ValidationResult {
  // 1. 空ファイルチェック
  if (file.size === 0) {
    return {
      isValid: false,
      error: 'ファイルが空です',
      errorCode: 'EMPTY_FILE'
    }
  }

  // 2. 危険な拡張子チェック
  if (hasDangerousExtension(file.name)) {
    return {
      isValid: false,
      error: `セキュリティ上の理由により、このファイル形式（${getFileExtension(file.name)}）はアップロードできません`,
      errorCode: 'DANGEROUS_EXTENSION'
    }
  }

  // 3. MIMEタイプチェック
  if (!isAllowedMimeType(file.type)) {
    return {
      isValid: false,
      error: `このファイル形式（${file.type || '不明'}）はサポートされていません。PDF、Word、Excel、PowerPoint、画像、テキストファイルのみアップロード可能です`,
      errorCode: 'INVALID_MIME_TYPE'
    }
  }

  // 4. 拡張子とMIMEタイプの一致チェック
  if (!isExtensionMatchingMimeType(file.name, file.type)) {
    return {
      isValid: false,
      error: `ファイル拡張子（${getFileExtension(file.name)}）とファイル形式が一致しません。ファイルが破損しているか、拡張子が偽装されている可能性があります`,
      errorCode: 'EXTENSION_MISMATCH'
    }
  }

  return { isValid: true }
}

/**
 * HTML input要素のaccept属性用の文字列を生成
 */
export function getAcceptAttribute(): string {
  const extensions = new Set<string>()

  Object.values(ALLOWED_FILE_TYPES).forEach(exts => {
    exts.forEach(ext => extensions.add(ext))
  })

  return Array.from(extensions).join(',')
}

/**
 * 画像ファイル専用のaccept属性
 */
export function getImageAcceptAttribute(): string {
  return '.jpg,.jpeg,.png,.gif,.webp'
}

/**
 * 許可されているファイル形式の説明テキストを取得
 */
export function getAllowedFileTypesDescription(language: 'ja' | 'en' = 'ja'): string {
  if (language === 'ja') {
    return 'PDF、Word、Excel、PowerPoint、テキスト、画像、ZIPファイル'
  }
  return 'PDF, Word, Excel, PowerPoint, Text, Images, ZIP files'
}
